import { useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useCollectionBinding } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

interface SlideshowImage {
  image: string;
  alt_text?: string;
  title?: string;
  original_url?: string;
}

/**
 * Auto-advancing image slideshow for the right half of the overview section.
 * Photos come from the property `photo_gallery` collection via a collection
 * binding, so editor reorder/hide/inject overrides apply. Falls back to
 * `original_url` for items that lack a CDN `image`.
 */
function OverviewSlideshow({ images }: { images: SlideshowImage[] }) {
  const [current, setCurrent] = useState(0);
  const count = images.length;

  const safeCurrent = count > 0 ? Math.min(current, count - 1) : 0;

  // Auto-advance every 5s; pauses while the tab is hidden.
  useEffect(() => {
    if (count <= 1) return;
    const id = window.setInterval(() => {
      setCurrent((p) => (p + 1) % count);
    }, 5000);
    return () => window.clearInterval(id);
  }, [count]);

  if (count === 0) {
    return (
      <div className="overview-slideshow overview-slideshow--empty">
        <p className="font-body text-sm text-[var(--vibe-color-text-muted)]">
          Gallery images will be displayed here.
        </p>
      </div>
    );
  }

  return (
    <div className="overview-slideshow">
      {images.map((img, i) => (
        <div
          key={img.image || img.original_url || i}
          className={`overview-slideshow__slide${i === safeCurrent ? ' is-active' : ''}`}
          aria-hidden={i === safeCurrent ? undefined : true}
        >
          <VibesitesImage
            url={img.image || img.original_url || ''}
            alt={img.alt_text || img.title || ''}
            className="w-full h-full object-cover"
            priority={i === 0}
          />
        </div>
      ))}
    </div>
  );
}

export function PropertyOverview() {
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);

  const slideshowImages = useCollectionBinding<SlideshowImage>('propertyoverview.slideshow', {
    collection: 'photo_gallery',
    defaultLimit: 8,
  });

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-12 md:pt-20 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left: title + copy */}
          <div ref={headingRef} className="reveal-fade-up text-center md:text-left md:pl-8 lg:pl-12">
            <RichTextContent
              path="propertyoverview.title"
              as="h2"
              className="text-2xl sm:text-3xl md:text-4xl font-bold overview-title mb-6 leading-tight"
            />

            <RichTextContent
              path="propertyoverview.description"
              as="div"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl mx-auto md:mx-0 leading-relaxed [&_a]:text-primary [&_a]:underline"
            />

            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="inline-block text-center mt-8 px-10 py-4 text-sm font-medium tracking-[0.1em] bg-[var(--vibe-color-secondary)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[color-mix(in_srgb,var(--vibe-color-secondary),black_12%)] transition-all"
              >
                {ctaLink.label || 'Explore Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Right: image slideshow */}
          <div className="reveal-fade-up md:pr-8 lg:pr-12">
            <OverviewSlideshow images={slideshowImages} />
          </div>
        </div>
      </div>

      <style>{`
        .overview-title {
          /* Slab/western display face for the section title, distinct from the
             hero title (which stays on the sans heading font). */
          font-family: "Josefin Slab", Rockwell, Georgia, serif;
          color: var(--vibe-color-text);
        }
        .overview-slideshow {
          position: relative;
          width: 100%;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
          aspect-ratio: 5 / 6;
          overflow: hidden;
          background: #000;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }
        .overview-slideshow--empty {
          display: flex;
          align-items: center;
          justify-content: center;
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, white);
          text-align: center;
          padding: 1.5rem;
        }

        .overview-slideshow__slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.6s ease;
        }
        .overview-slideshow__slide.is-active {
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        }

        @media (prefers-reduced-motion: reduce) {
          .overview-slideshow__slide { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'propertyoverview.slideshow': {
      label: 'Slideshow Photos',
      collection: 'photo_gallery',
      defaultLimit: 8,
      description: 'Images shown in the overview slideshow, pulled from the property photo gallery.',
    },
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
